/**
 * theme.ts
 * Pure theme helpers (no React): preference storage, resolution and DOM application.
 * public/theme-init.js duplicates THEME_STORAGE_KEY, DARK_CLASS and THEME_COLORS on purpose
 * (a classic pre-paint script cannot import); a unit test guards that they match.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'resida.landlord.theme';
export const DARK_CLASS = 'dark';

/** Hex of --background per theme (asserted against tokens.css in design-tokens.test.ts). */
export const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#191715',
};

const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export function isThemePreference(v: unknown): v is ThemePreference {
  return typeof v === 'string' && (PREFERENCES as readonly string[]).includes(v);
}

function defaultStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function readStoredPreference(storage?: Pick<Storage, 'getItem'>): ThemePreference {
  try {
    const store = storage ?? defaultStorage();
    const value = store?.getItem(THEME_STORAGE_KEY);
    return isThemePreference(value) ? value : 'system';
  } catch {
    return 'system';
  }
}

export function writeStoredPreference(
  pref: ThemePreference,
  storage?: Pick<Storage, 'setItem'>,
): boolean {
  try {
    const store = storage ?? defaultStorage();
    if (!store) return false;
    store.setItem(THEME_STORAGE_KEY, pref);
    return true;
  } catch {
    return false;
  }
}

export function systemPrefersDark(win?: Pick<Window, 'matchMedia'>): boolean {
  try {
    const w = win ?? (typeof window === 'undefined' ? undefined : window);
    if (!w || typeof w.matchMedia !== 'function') return false;
    return w.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function resolveTheme(pref: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (pref === 'system') return prefersDark ? 'dark' : 'light';
  return pref;
}

export function applyTheme(resolved: ResolvedTheme, doc: Document = document): void {
  const root = doc.documentElement;
  root.classList.toggle(DARK_CLASS, resolved === 'dark');
  root.style.colorScheme = resolved;
  let meta = doc.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = doc.createElement('meta');
    meta.name = 'theme-color';
    doc.head.appendChild(meta);
  }
  meta.content = THEME_COLORS[resolved];
}
